/**
 * CompressionAdvisor — "¿comprimo este ore antes de haul-ear?"
 *
 *  - Settings (compression_tax + hauling_cost) en localStorage, auto-save.
 *  - Re-fetch cuando los settings cambian.
 *  - Verdict por ore: compress / sell_uncompressed / indifferent / ya-comprimido.
 *  - Summary tiles con m³ saved, total compression cost, hauling value saved,
 *    net benefit.
 *  - Caveat: volumes vienen de ESI (cached, ~0 subrequests warm). Arkonor /
 *    Mercoxit no tienen variant comprimida → "no aplica".
 */

import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, formatISK, formatISKShort } from '../api'
import { LastUpdated } from './LastUpdated'
import { RefreshButton } from './RefreshButton'
import { LoadingSkeleton } from './LoadingSkeleton'
import { useForceRefetch } from '../hooks/useForceRefetch'
import {
  type CompressionSettings,
  DEFAULT_COMPRESSION_SETTINGS,
  loadCompressionSettings,
  saveCompressionSettings,
} from '../lib/compressionSettings'

type Verdict = 'compress' | 'sell_uncompressed' | 'indifferent' | 'insufficient_data'

type Item = {
  type_id: number
  name: string
  form: 'uncompressed' | 'compressed' | 'unknown'
  quantity: number
  compressed_type_id: number | null
  compressed_qty_equivalent: number | null
  volume_per_unit: number | null
  volume_total_m3: number | null
  compressed_volume_per_unit: number | null
  compressed_volume_total_m3: number | null
  m3_saved: number | null
  jita_sell_price: number | null
  jita_compressed_sell_price: number | null
  sell_value_total: number | null
  compressed_value_total: number | null
  compression_cost: number | null
  hauling_value_saved: number | null
  net_benefit: number | null
  verdict: Verdict
  reason: string
}

const VERDICT_FILTERS: Array<{ key: Verdict | 'all'; label: string }> = [
  { key: 'all', label: 'Todos' },
  { key: 'compress', label: 'Comprimir' },
  { key: 'sell_uncompressed', label: 'No comprimir' },
  { key: 'indifferent', label: 'Indiferente' },
  { key: 'insufficient_data', label: 'Sin datos' },
]

function verdictClasses(v: Verdict): string {
  switch (v) {
    case 'compress':
      return 'bg-eve-success/10 border-eve-success text-eve-success'
    case 'sell_uncompressed':
      return 'bg-eve-danger/10 border-eve-danger text-eve-danger'
    case 'indifferent':
      return 'bg-eve-muted/10 border-eve-border text-eve-muted'
    case 'insufficient_data':
      return 'bg-eve-accent/10 border-eve-accent text-eve-accent'
  }
}

function verdictLabel(v: Verdict): string {
  switch (v) {
    case 'compress':
      return 'Comprimir'
    case 'sell_uncompressed':
      return 'No comprimir'
    case 'indifferent':
      return 'Indiferente'
    case 'insufficient_data':
      return 'Sin datos'
  }
}

export function CompressionAdvisor() {
  const [settings, setSettings] = useState<CompressionSettings>(() => loadCompressionSettings())
  const [verdictFilter, setVerdictFilter] = useState<Verdict | 'all'>('all')
  const { key: forceRefetchKey, force, bump } = useForceRefetch()

  // Auto-save en cada cambio de settings.
  useEffect(() => {
    saveCompressionSettings(settings)
  }, [settings])

  const { data, isLoading, dataUpdatedAt, isFetching } = useQuery({
    queryKey: [
      'utility-compression-advice',
      forceRefetchKey,
      settings.compression_tax_per_unit,
      settings.hauling_cost_per_m3,
    ],
    queryFn: () =>
      api.utilityCompressionAdvice(
        {
          compression_tax: settings.compression_tax_per_unit,
          hauling_cost: settings.hauling_cost_per_m3,
        },
        force,
      ),
  })

  const items = data?.items ?? []
  const summary = data?.summary

  const filtered = useMemo(() => {
    return items.filter((it) => {
      if (verdictFilter !== 'all' && it.verdict !== verdictFilter) return false
      return true
    })
  }, [items, verdictFilter])

  return (
    <div className="card">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="stat-label">Compression advisor</div>
          <div className="text-eve-muted text-xs">
            ¿Conviene comprimir antes de haul-ear? Settings abajo.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <LastUpdated timestamp={dataUpdatedAt} label="" />
          <RefreshButton onClick={bump} isFetching={isFetching} />
        </div>
      </div>

      {/* Settings — compression tax + hauling cost */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4 p-3 bg-eve-panel/40 border border-eve-border rounded-md">
        <NumberField
          label="Compression tax (ISK / unidad uncompressed)"
          value={settings.compression_tax_per_unit}
          onChange={(v) => setSettings((s) => ({ ...s, compression_tax_per_unit: v }))}
          hint="Player structure con rigs ≈ 0. NPC station ≈ 5-10."
        />
        <NumberField
          label="Hauling cost (ISK / m³)"
          value={settings.hauling_cost_per_m3}
          onChange={(v) => setSettings((s) => ({ ...s, hauling_cost_per_m3: v }))}
          hint="Own freighter ≈ 0. Public courier ≈ 500-2000."
        />
        <div className="col-span-1 sm:col-span-2 flex justify-end">
          <button
            onClick={() => setSettings(DEFAULT_COMPRESSION_SETTINGS)}
            className="text-[11px] uppercase tracking-wider text-eve-muted hover:text-eve-accent border border-eve-border hover:border-eve-accent/40 px-2 py-0.5 rounded transition-colors"
          >
            ↻ Reset a 0/0
          </button>
        </div>
      </div>

      {/* Summary tiles */}
      {summary && summary.total_ores > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <SummaryTile
            label="Ores analizados"
            value={String(summary.total_ores)}
            sub={
              summary.compress_wins > 0 || summary.sell_wins > 0
                ? `${summary.compress_wins} comprimir · ${summary.sell_wins} no`
                : '—'
            }
          />
          <SummaryTile
            label="m³ saved (si comprís)"
            value={formatM3(summary.total_m3_saved)}
            sub="volumen total ahorrado"
          />
          <SummaryTile
            label="Compression cost total"
            value={`${formatISKShort(summary.total_compression_cost)} ISK`}
            sub="si comprís todos los que rinde"
          />
          <SummaryTile
            label="Net benefit (comprimir todos)"
            value={`${
              summary.total_net_benefit >= 0 ? '+' : ''
            }${formatISKShort(summary.total_net_benefit)} ISK`}
            sub={`saved ${formatISKShort(summary.total_hauling_value_saved)} ISK`}
            positive={summary.total_net_benefit > 0}
            negative={summary.total_net_benefit < 0}
          />
        </div>
      )}

      {/* Verdict filter */}
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <select
          value={verdictFilter}
          onChange={(e) => setVerdictFilter(e.target.value as Verdict | 'all')}
          className="bg-eve-panel border border-eve-border rounded-md px-2 py-1.5 text-sm text-eve-text focus:border-eve-accent focus:outline-none"
        >
          {VERDICT_FILTERS.map((f) => (
            <option key={f.key} value={f.key}>
              {f.label}
            </option>
          ))}
        </select>
        <span className="text-eve-muted text-xs ml-auto">
          {filtered.length} / {items.length}
        </span>
      </div>

      {/* List */}
      {isLoading ? (
        <LoadingSkeleton variant="rows" rows={5} />
      ) : data?.location?.kind === 'in_space' ? (
        <Empty msg="Estás en space — volve a dockear para leer el hangar." />
      ) : items.length === 0 ? (
        <Empty msg="No tenés ore en el hangar dockeado." />
      ) : filtered.length === 0 ? (
        <Empty msg="Sin matches para el filtro actual." />
      ) : (
        <div className="space-y-2">
          {filtered.map((it) => (
            <CompressionRow key={it.type_id} item={it} />
          ))}
        </div>
      )}

      <p className="text-xs text-eve-muted mt-3">
        Arkonor y Mercoxit no tienen variant comprimida en EVE — no se pueden
        comprimir. Para todos los demás: 1 unidad compressed = 100 unidades
        uncompressed equivalent (regla fija).
      </p>
    </div>
  )
}

// ============================================================================
// Sub-components
// ============================================================================

function NumberField({
  label,
  value,
  onChange,
  hint,
}: {
  label: string
  value: number
  onChange: (v: number) => void
  hint?: string
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-eve-muted text-xs">{label}</span>
      <input
        type="number"
        min={0}
        step={1}
        value={value}
        onChange={(e) => {
          const n = parseFloat(e.target.value)
          onChange(Number.isFinite(n) && n >= 0 ? n : 0)
        }}
        className="bg-eve-bg border border-eve-border rounded-md px-3 py-1.5 text-sm text-eve-text focus:border-eve-accent focus:outline-none font-mono"
      />
      {hint && <span className="text-eve-muted text-[10px] italic">{hint}</span>}
    </label>
  )
}

function SummaryTile({
  label,
  value,
  sub,
  positive,
  negative,
}: {
  label: string
  value: string
  sub?: string
  positive?: boolean
  negative?: boolean
}) {
  const valueClass = positive
    ? 'text-eve-success'
    : negative
      ? 'text-eve-danger'
      : 'text-eve-text'
  return (
    <div className="bg-eve-bg border border-eve-border rounded-md p-3">
      <div className="stat-label">{label}</div>
      <div className={`font-mono text-lg font-semibold mt-1 ${valueClass}`}>{value}</div>
      {sub && <div className="text-[10px] text-eve-muted mt-1 italic">{sub}</div>}
    </div>
  )
}

function CompressionRow({ item }: { item: Item }) {
  const verdict = item.verdict
  const uncompressedVolume = item.volume_total_m3
  const compressedVolume = item.compressed_volume_total_m3
  const isCompressedForm = item.form === 'compressed'

  return (
    <details className="border border-eve-border rounded-md bg-eve-bg/40">
      <summary className="cursor-pointer p-3 flex items-center gap-3 hover:bg-eve-panel/50 transition-colors">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="font-medium">{item.name}</span>
            <span className="text-xs text-eve-muted font-mono">
              × {item.quantity.toLocaleString('en-US')}
            </span>
            {isCompressedForm && (
              <span className="text-[10px] uppercase font-mono px-1.5 py-0.5 rounded bg-eve-accent/10 border border-eve-accent/30 text-eve-accent">
                ya comprimido
              </span>
            )}
          </div>
          <div className="text-xs text-eve-muted mt-1">{item.reason}</div>
        </div>
        <div className="flex items-center gap-3">
          {!isCompressedForm && item.m3_saved !== null && (
            <PillColumn
              label="m³ saved"
              value={formatM3(item.m3_saved)}
              sub={`${formatM3(uncompressedVolume)} → ${formatM3(compressedVolume)}`}
            />
          )}
          {!isCompressedForm && item.net_benefit !== null && (
            <PillColumn
              label="Net benefit"
              value={`${
                item.net_benefit >= 0 ? '+' : ''
              }${formatISKShort(item.net_benefit)} ISK`}
              positive={item.net_benefit > 0}
              negative={item.net_benefit < 0}
            />
          )}
          <span
            className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border whitespace-nowrap ${verdictClasses(verdict)}`}
          >
            {verdictLabel(verdict)}
          </span>
        </div>
      </summary>
      <div className="border-t border-eve-border px-3 py-3 text-xs space-y-1">
        <Row label="Forma" value={isCompressedForm ? 'Compressed' : 'Uncompressed'} />
        {!isCompressedForm && (
          <>
            <Row
              label="Volume actual"
              value={
                uncompressedVolume !== null
                  ? `${formatM3(uncompressedVolume)} (${item.volume_per_unit?.toFixed(2)} m³/u)`
                  : '—'
              }
            />
            <Row
              label="Volume compressed"
              value={
                compressedVolume !== null
                  ? `${formatM3(compressedVolume)} (${item.compressed_volume_per_unit?.toFixed(2)} m³/u × ${item.compressed_qty_equivalent} u)`
                  : '—'
              }
            />
            <Row
              label="m³ saved"
              value={item.m3_saved !== null ? formatM3(item.m3_saved) : '—'}
            />
            <Row
              label="Compression cost"
              value={
                item.compression_cost !== null
                  ? `${formatISK(item.compression_cost, 0)} ISK`
                  : '—'
              }
            />
            <Row
              label="Hauling value saved"
              value={
                item.hauling_value_saved !== null
                  ? `${formatISK(item.hauling_value_saved, 0)} ISK`
                  : '—'
              }
            />
            <Row
              label="Net benefit"
              value={
                item.net_benefit !== null
                  ? `${formatISK(item.net_benefit, 0)} ISK`
                  : '—'
              }
              bold
            />
          </>
        )}
        <Row
          label="Jita sell (uncompressed)"
          value={
            item.jita_sell_price !== null ? formatISK(item.jita_sell_price, 2) : '—'
          }
        />
        <Row
          label="Jita sell (compressed)"
          value={
            item.jita_compressed_sell_price !== null
              ? formatISK(item.jita_compressed_sell_price, 2)
              : '—'
          }
        />
      </div>
    </details>
  )
}

function PillColumn({
  label,
  value,
  sub,
  positive,
  negative,
}: {
  label: string
  value: string
  sub?: string
  positive?: boolean
  negative?: boolean
}) {
  const valueClass = positive
    ? 'text-eve-success'
    : negative
      ? 'text-eve-danger'
      : 'text-eve-text'
  return (
    <div className="text-right">
      <div className="text-[10px] text-eve-muted uppercase font-mono">{label}</div>
      <div className={`text-sm font-mono font-semibold ${valueClass}`}>{value}</div>
      {sub && <div className="text-[9px] text-eve-muted font-mono">{sub}</div>}
    </div>
  )
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className={`flex justify-between gap-2 ${bold ? 'font-semibold' : ''}`}>
      <span className="text-eve-muted">{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  )
}

function Empty({ msg }: { msg: string }) {
  return <div className="text-eve-muted py-8 text-center italic">{msg}</div>
}

function formatM3(n: number | null): string {
  if (n === null) return '—'
  if (n >= 1000) return `${(n / 1000).toFixed(2)}k m³`
  if (n >= 1) return `${n.toFixed(2)} m³`
  return `${(n * 100).toFixed(1)} L`
}